/**
 * Procurement API service
 */
import axiosInstance from '../../../shared/api/axiosInstance';
import type { GoodsReceiptAttachmentKind } from '@/generated/api/models/goodsReceiptAttachmentKind';
import type { GoodsReceiptAttachmentListResponse } from '@/generated/api/models/goodsReceiptAttachmentListResponse';
import type { RfqSupplierTimeline } from '@/generated/api/models/rfqSupplierTimeline';
import type { RfqSupplierTimelineResponse } from '@/generated/api/models/rfqSupplierTimelineResponse';
import type {
  ProcurementPage,
  VendorProfile,
  VendorCreate,
  VendorUpdate,
  ListVendorsParams,
  VendorRowListResponse,
  BulkCreateProfilesRequest,
  BulkCreateProfilesResponse,
  PurchaseRequisition,
  RequisitionCreate,
  RequisitionUpdate,
  ConvertRequisitionRequest,
  ListRequisitionsParams,
  PurchaseOrder,
  ListVendorOrdersParams,
  VendorOrderListResponse,
  PurchaseOrderCreate,
  PurchaseOrderUpdate,
  PurchaseOrderEmailRequest,
  InstallmentPrepayRequest,
  InstallmentPrepayResult,
  ListPurchaseOrdersParams,
  PurchaseOrderPage,
  GoodsReceipt,
  GoodsReceiptCreate,
  ReceiptReasonCode,
  UnmatchedReceiptReasonCode,
  ListGoodsReceiptsParams,
  LicensePlate,
  LicensePlateCreate,
  LicensePlateStatus,
  ThreeWayMatchReport,
  LandedCostRollup,
  LandedCostApplyResult,
  SupplierClaim,
  SupplierClaimCreate,
  SupplierClaimUpdate,
  SupplierClaimTransition,
  ListSupplierClaimsParams,
  VendorReturn,
  VendorReturnCreate,
  VendorReturnUpdate,
  CreateVendorCreditResult,
  ListVendorReturnsParams,
  ProcurementDashboard,
  ProcurementTenantSettings,
  RecurringPoTemplate,
  RecurringPoTemplateCreate,
  RecurringPoTemplateUpdate,
  ListRecurringPoTemplatesParams,
  OverdueReport,
  AnticipatedReceiptsReport,
  PoHistoryReport,
  PurchasesReport,
  ReportAdminScopeParams,
  ReportDateRangeParams,
  ReportPagingParams,
  Rfq,
  RfqStatus,
  RfqCreate,
  RfqUpdate,
  RfqFromSalesOrderRequest,
  RfqSendOutcome,
  RfqSupplierGroup,
  RfqResponseState,
  ListRfqsParams,
  SupplierPi,
  RfqComparison,
  UncontestedAwardResult,
} from '../types';

const BASE_URL = '/api/v1/procurement';

// ─── Vendor API ────────────────────────────────────────────────

export const vendorApi = {
  list: async (params?: ListVendorsParams): Promise<VendorRowListResponse> => {
    const response = await axiosInstance.get(`${BASE_URL}/vendors`, { params });
    return response.data as VendorRowListResponse;
  },

  get: async (id: string): Promise<VendorProfile> => {
    const response = await axiosInstance.get(`${BASE_URL}/vendors/${id}`);
    return response.data.data;
  },

  create: async (data: VendorCreate): Promise<VendorProfile> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendors`, data);
    return response.data.data;
  },

  bulkCreateProfiles: async (
    data: BulkCreateProfilesRequest,
  ): Promise<BulkCreateProfilesResponse> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendors/bulk-create-profiles`, data);
    return response.data as BulkCreateProfilesResponse;
  },

  update: async (id: string, data: VendorUpdate): Promise<VendorProfile> => {
    const response = await axiosInstance.patch(`${BASE_URL}/vendors/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/vendors/${id}`);
  },

  // Takes the vendor *profile* id; the backend resolves its organization_id (the
  // handle `proc_purchase_orders.vendor_id` actually keys on) and applies the
  // vendor's org-visibility gate. Server-side paginated — a vendor's PO history
  // grows without bound, so the endpoint always applies a limit.
  listOrders: async (
    id: string,
    params?: ListVendorOrdersParams,
  ): Promise<VendorOrderListResponse> => {
    const response = await axiosInstance.get(`${BASE_URL}/vendors/${id}/orders`, { params });
    return response.data;
  },
};

// ─── Requisition API ───────────────────────────────────────────

export const requisitionApi = {
  // Server-paged (Shape A): one page of rows plus the size of the whole filtered
  // set, so the pager can never be driven by the row count.
  list: async (params?: ListRequisitionsParams): Promise<ProcurementPage<PurchaseRequisition>> => {
    const response = await axiosInstance.get(`${BASE_URL}/requisitions`, { params });
    return { items: response.data.data, total: response.data.pagination.total };
  },

  get: async (id: string): Promise<PurchaseRequisition> => {
    const response = await axiosInstance.get(`${BASE_URL}/requisitions/${id}`);
    return response.data.data;
  },

  create: async (data: RequisitionCreate): Promise<PurchaseRequisition> => {
    const response = await axiosInstance.post(`${BASE_URL}/requisitions`, data);
    return response.data.data;
  },

  update: async (id: string, data: RequisitionUpdate): Promise<PurchaseRequisition> => {
    const response = await axiosInstance.patch(`${BASE_URL}/requisitions/${id}`, data);
    return response.data.data;
  },

  submit: async (id: string): Promise<PurchaseRequisition> => {
    const response = await axiosInstance.post(`${BASE_URL}/requisitions/${id}/submit`);
    return response.data.data;
  },

  approve: async (id: string): Promise<PurchaseRequisition> => {
    const response = await axiosInstance.post(`${BASE_URL}/requisitions/${id}/approve`);
    return response.data.data;
  },

  reject: async (id: string): Promise<PurchaseRequisition> => {
    const response = await axiosInstance.post(`${BASE_URL}/requisitions/${id}/reject`);
    return response.data.data;
  },

  convertToPO: async (
    id: string,
    payload: ConvertRequisitionRequest
  ): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/requisitions/${id}/convert`,
      payload
    );
    return response.data.data;
  },
};

// ─── Purchase Order API ────────────────────────────────────────

export const purchaseOrderApi = {
  // Returns the whole envelope, not just the rows: purchase orders accumulate, so
  // the list page pages through this and drives its pager from `pagination.total`
  // (ui-list-pages.md § 11). Callers that only want rows read `.data`.
  list: async (params?: ListPurchaseOrdersParams): Promise<PurchaseOrderPage> => {
    const response = await axiosInstance.get(`${BASE_URL}/orders`, { params });
    return response.data as PurchaseOrderPage;
  },

  get: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.get(`${BASE_URL}/orders/${id}`);
    return response.data.data;
  },

  create: async (data: PurchaseOrderCreate): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders`, data);
    return response.data.data;
  },

  update: async (id: string, data: PurchaseOrderUpdate): Promise<PurchaseOrder> => {
    const response = await axiosInstance.patch(`${BASE_URL}/orders/${id}`, data);
    return response.data.data;
  },

  approve: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/approve`);
    return response.data.data;
  },

  // Draft-only, enforced server-side (409 CANNOT_DELETE past draft or once stock
  // has been received). Soft delete — the record leaves every view but stays
  // recoverable in the retention window. 204, so there is no body to unwrap.
  remove: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/orders/${id}`);
  },

  send: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/send`);
    return response.data.data;
  },

  cancel: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/cancel`);
    return response.data.data;
  },

  close: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/close`);
    return response.data.data;
  },

  copy: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/copy`);
    return response.data.data;
  },

  releaseBlanket: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/release-blanket`);
    return response.data.data;
  },

  stageVoucher: async (id: string): Promise<{ id: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${id}/stage-voucher`);
    return response.data.data;
  },

  email: async (id: string, data: PurchaseOrderEmailRequest): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/orders/${id}/email`, data);
  },

  prepayInstallment: async (
    orderId: string,
    installmentId: string,
    data: InstallmentPrepayRequest
  ): Promise<InstallmentPrepayResult> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/orders/${orderId}/installments/${installmentId}/prepay`,
      data
    );
    return response.data.data;
  },

  /** `layoutId` picks a named line-column layout; omitted takes the type's default. */
  downloadPdf: async (id: string, poNumber: string, layoutId?: string | null): Promise<void> => {
    const response = await axiosInstance.get(`${BASE_URL}/orders/${id}/pdf`, {
      responseType: 'blob',
      params: layoutId ? { layout_id: layoutId } : undefined,
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${poNumber.replace(/\//g, '-')}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  },
};

// ─── RFQ API ───────────────────────────────────────────────────

export const rfqApi = {
  // Server-paged (Shape A). `indexes: null` so a multi-value `status` serializes
  // as the repeatable `?status=a&status=b` FastAPI reads, not `status[]=a`.
  list: async (params?: ListRfqsParams): Promise<ProcurementPage<Rfq>> => {
    const response = await axiosInstance.get(`${BASE_URL}/rfqs`, {
      params,
      paramsSerializer: { indexes: null },
    });
    return { items: response.data.data, total: response.data.pagination.total };
  },

  get: async (id: string): Promise<Rfq> => {
    const response = await axiosInstance.get(`${BASE_URL}/rfqs/${id}`);
    return response.data.data;
  },

  create: async (data: RfqCreate): Promise<Rfq> => {
    const response = await axiosInstance.post(`${BASE_URL}/rfqs`, data);
    return response.data.data;
  },

  createFromSalesOrder: async (data: RfqFromSalesOrderRequest): Promise<Rfq> => {
    const response = await axiosInstance.post(`${BASE_URL}/rfqs/from-sales-order`, data);
    return response.data.data;
  },

  update: async (id: string, data: RfqUpdate): Promise<Rfq> => {
    const response = await axiosInstance.put(`${BASE_URL}/rfqs/${id}`, data);
    return response.data.data;
  },

  setSuppliers: async (id: string, suppliers: RfqSupplierGroup[]): Promise<Rfq> => {
    const response = await axiosInstance.post(`${BASE_URL}/rfqs/${id}/suppliers`, { suppliers });
    return response.data.data;
  },

  /**
   * Every invited supplier's history with this RFQ — dispatches (including
   * re-sends), bids and a capped correspondence summary — in ONE request.
   *
   * Answered for the whole RFQ rather than per invitation because the Suppliers
   * panel renders a collapsible per supplier and a buyer opens several; a route
   * per invitation is 25 round trips on a full fan-out.
   */
  supplierTimelines: async (id: string): Promise<RfqSupplierTimeline[]> => {
    const response = await axiosInstance.get<RfqSupplierTimelineResponse>(
      `${BASE_URL}/rfqs/${id}/supplier-timeline`,
    );
    return response.data.data;
  },

  /**
   * `layoutId` picks a named line-column layout for the attached RFQ PDF;
   * `message` is the buyer's own covering note, spliced above the document in
   * every supplier's email (sent verbatim — never translated).
   *
   * `supplierIds` narrows the fan-out to the invitations the buyer ticked.
   * Undefined sends to every supplier still awaiting dispatch — the whole-RFQ
   * behaviour this call has always had, which the SO one-shot create+send path
   * still relies on.
   */
  send: async (
    id: string,
    layoutId?: string | null,
    message?: string | null,
    supplierIds?: string[] | null,
    includeSettled?: boolean,
    inviteToPortal?: boolean,
  ): Promise<RfqSendOutcome> => {
    const response = await axiosInstance.post(`${BASE_URL}/rfqs/${id}/send`, {
      layout_id: layoutId ?? null,
      message: message?.trim() ? message.trim() : null,
      supplier_ids: supplierIds ?? null,
      include_settled: includeSettled ?? false,
      invite_to_portal: inviteToPortal ?? false,
    });
    // The dispatch is deferred and deduplicated per invitation, so what the send
    // ACTUALLY did is not derivable from the RFQ it returns — an invitation with a
    // send already in flight is skipped, and under an explicit "email this supplier
    // again" that would otherwise be a success toast and no second email.
    return { rfq: response.data.data, dispatch: response.data.dispatch };
  },

  /**
   * Mint the public link the buyer hands ONE supplier over WhatsApp or WeChat.
   *
   * Procurement's own door onto the shared distribution seam rather than the
   * generic `/document-distribution/...` route: an RFQ is company-scoped and
   * that route can only enforce tenant + module access. Returns the same shape,
   * so `buildWhatsAppUrl` from `shared/documents` consumes it unchanged.
   */
  supplierWhatsAppLink: async (
    id: string,
    rfqSupplierId: string,
    linkFormat: 'view' | 'pdf' = 'pdf',
  ): Promise<{ public_url: string; summary_text: string }> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/rfqs/${id}/suppliers/${rfqSupplierId}/whatsapp-link`,
      undefined,
      { params: { link_format: linkFormat } },
    );
    return response.data;
  },

  cancel: async (id: string): Promise<Rfq> => {
    const response = await axiosInstance.post(`${BASE_URL}/rfqs/${id}/cancel`);
    return response.data.data;
  },

  /**
   * Take an awarded RFQ back out of `awarded` for genuine re-sourcing (§8b).
   *
   * The ESCAPE, not the default — a factory's CORRECTED quote is a revision
   * recorded from the winning quote itself and never re-opens competition. The
   * reason is required and audited; the call refuses while a live back-to-back
   * purchase order already sources from this RFQ's winners.
   */
  reopen: async (
    id: string,
    reason: string,
  ): Promise<{ id: string; status: RfqStatus; response_rollup: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/rfqs/${id}/reopen`, { reason });
    return response.data.data;
  },

  recordSupplierResponse: async (
    id: string,
    rfqSupplierId: string,
    responseState: RfqResponseState,
  ): Promise<Rfq> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/rfqs/${id}/suppliers/${rfqSupplierId}/response`,
      { response_state: responseState },
    );
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/rfqs/${id}`);
  },

  /**
   * Download the exact branded PDF ONE supplier receives.
   *
   * Per supplier, not per RFQ: an RFQ renders once per invitation, against that
   * supplier's own line subset, pinned layout and language — so an RFQ-level
   * download would be a document nobody was ever sent. `layoutId` picks a named
   * line-column layout, the same parameter `send` takes, so the buyer can
   * compare a preview and a send like for like.
   */
  downloadSupplierPdf: async (
    id: string,
    rfqSupplierId: string,
    filenameStem: string,
    layoutId?: string | null,
  ): Promise<void> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/rfqs/${id}/suppliers/${rfqSupplierId}/pdf`,
      {
        responseType: 'blob',
        params: layoutId ? { layout_id: layoutId } : undefined,
      },
    );
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenameStem.replace(/[/\\]/g, '-')}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Download SEVERAL suppliers' RFQ PDFs as one zip.
   *
   * An RFQ renders once per supplier, so "download the ones I ticked" is N
   * files — and a browser refuses to fire N downloads from one gesture, which is
   * why the server archives them rather than the client looping. `supplierIds`
   * undefined downloads every invitation on the RFQ.
   */
  downloadSupplierPdfBundle: async (
    id: string,
    filenameStem: string,
    supplierIds?: string[],
    layoutId?: string | null,
  ): Promise<void> => {
    const response = await axiosInstance.get(`${BASE_URL}/rfqs/${id}/suppliers/pdf-bundle`, {
      responseType: 'blob',
      params: {
        ...(supplierIds?.length ? { supplier_ids: supplierIds } : {}),
        ...(layoutId ? { layout_id: layoutId } : {}),
      },
      // Repeatable params, not `a,b` — the route declares `list[UUID]`, and a
      // comma-joined string reaches it as one malformed id.
      paramsSerializer: { indexes: null },
    });
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: 'application/zip' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenameStem.replace(/[/\\]/g, '-')}.zip`;
    a.click();
    window.URL.revokeObjectURL(url);
  },
};

/** The branded-HTML twin of `rfqApi.downloadSupplierPdf`, for `useDocumentPreviewHtml`. */
export const rfqSupplierPreviewPath = (id: string, rfqSupplierId: string): string =>
  `${BASE_URL}/rfqs/${id}/suppliers/${rfqSupplierId}/preview`;

// ─── Supplier PI (proforma invoice) & comparison API ([OC&CO] A4) ──────────

export const supplierPiApi = {
  comparison: async (rfqId: string): Promise<RfqComparison> => {
    const response = await axiosInstance.get(`${BASE_URL}/rfqs/${rfqId}/comparison`);
    return response.data.data;
  },

  get: async (piId: string): Promise<SupplierPi> => {
    const response = await axiosInstance.get(`${BASE_URL}/supplier-pis/${piId}`);
    return response.data.data;
  },

  selectWinner: async (rfqId: string, rfqLineId: string, piLineId: string): Promise<Rfq> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/rfqs/${rfqId}/lines/${rfqLineId}/winner`,
      { pi_line_id: piLineId },
    );
    return response.data.data;
  },

  // Point a quoted PI line at a demand line, or unset it (null). The write behind
  // the comparison grid's "additional quoted lines" section ([OC&CO] M5-8).
  setQuoteDemandLine: async (
    piId: string,
    piLineId: string,
    rfqLineId: string | null,
  ): Promise<SupplierPi> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/supplier-pis/${piId}/lines/${piLineId}/demand-line`,
      { rfq_line_id: rfqLineId },
    );
    return response.data.data;
  },

  clearWinner: async (rfqId: string, rfqLineId: string): Promise<Rfq> => {
    const response = await axiosInstance.delete(
      `${BASE_URL}/rfqs/${rfqId}/lines/${rfqLineId}/winner`,
    );
    return response.data.data;
  },

  awardUncontested: async (rfqId: string): Promise<UncontestedAwardResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/rfqs/${rfqId}/winners/uncontested`);
    return response.data.data;
  },
};

// ─── Recurring PO Template API ─────────────────────────────────

export const recurringPoTemplateApi = {
  list: async (params?: ListRecurringPoTemplatesParams): Promise<RecurringPoTemplate[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/recurring-po-templates`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<RecurringPoTemplate> => {
    const response = await axiosInstance.get(`${BASE_URL}/recurring-po-templates/${id}`);
    return response.data.data;
  },

  create: async (data: RecurringPoTemplateCreate): Promise<RecurringPoTemplate> => {
    const response = await axiosInstance.post(`${BASE_URL}/recurring-po-templates`, data);
    return response.data.data;
  },

  update: async (id: string, data: RecurringPoTemplateUpdate): Promise<RecurringPoTemplate> => {
    const response = await axiosInstance.patch(`${BASE_URL}/recurring-po-templates/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/recurring-po-templates/${id}`);
  },

  run: async (id: string): Promise<PurchaseOrder> => {
    const response = await axiosInstance.post(`${BASE_URL}/recurring-po-templates/${id}/run`);
    return response.data.data;
  },
};

// ─── Reports API ───────────────────────────────────────────────

// The three PO-level reports are server-paged (Shape A): `orders` is ONE page,
// while `count` and `totals_by_currency` describe the whole filtered set.
export const procurementReportApi = {
  overdue: async (
    params?: ReportPagingParams & ReportAdminScopeParams & { as_of?: string },
  ): Promise<OverdueReport> => {
    const response = await axiosInstance.get(`${BASE_URL}/reports/overdue`, { params });
    return response.data.data;
  },

  anticipatedReceipts: async (
    params?: ReportDateRangeParams & ReportPagingParams & ReportAdminScopeParams,
  ): Promise<AnticipatedReceiptsReport> => {
    const response = await axiosInstance.get(`${BASE_URL}/reports/anticipated-receipts`, { params });
    return response.data.data;
  },

  poHistory: async (
    params?: ReportDateRangeParams & ReportPagingParams & ReportAdminScopeParams & { vendor_id?: string },
  ): Promise<PoHistoryReport> => {
    const response = await axiosInstance.get(`${BASE_URL}/reports/po-history`, { params });
    return response.data.data;
  },

  purchases: async (
    params?: ReportDateRangeParams & ReportAdminScopeParams,
  ): Promise<PurchasesReport> => {
    const response = await axiosInstance.get(`${BASE_URL}/reports/purchases`, { params });
    return response.data.data;
  },
};

// ─── Goods Receipt API ─────────────────────────────────────────

export const goodsReceiptApi = {
  // Server-paged (Shape A): one page of rows plus the size of the whole filtered
  // set, so the pager can never be driven by the row count.
  list: async (params?: ListGoodsReceiptsParams): Promise<ProcurementPage<GoodsReceipt>> => {
    const response = await axiosInstance.get(`${BASE_URL}/receipts`, { params });
    return { items: response.data.data, total: response.data.pagination.total };
  },

  get: async (id: string): Promise<GoodsReceipt> => {
    const response = await axiosInstance.get(`${BASE_URL}/receipts/${id}`);
    return response.data.data;
  },

  create: async (data: GoodsReceiptCreate): Promise<GoodsReceipt> => {
    const response = await axiosInstance.post(`${BASE_URL}/receipts`, data);
    return response.data.data;
  },

  receive: async (id: string, confirmOverReceipt = false): Promise<GoodsReceipt> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/receipts/${id}/receive`,
      undefined,
      { params: { confirm_over_receipt: confirmOverReceipt } },
    );
    return response.data.data;
  },

  cancel: async (id: string): Promise<GoodsReceipt> => {
    const response = await axiosInstance.post(`${BASE_URL}/receipts/${id}/cancel`);
    return response.data.data;
  },

  update: async (
    id: string,
    data: Partial<GoodsReceiptCreate> & Record<string, unknown>,
  ): Promise<GoodsReceipt> => {
    const response = await axiosInstance.patch(`${BASE_URL}/receipts/${id}`, data);
    return response.data.data;
  },

  downloadTicketPdf: async (id: string, receiptNumber: string): Promise<void> => {
    const response = await axiosInstance.get(`${BASE_URL}/receipts/${id}/pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `Receiving-Ticket-${receiptNumber.replace(/\//g, '-')}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  },

  downloadLpLabels: async (id: string, receiptNumber: string): Promise<void> => {
    const response = await axiosInstance.get(`${BASE_URL}/receipts/${id}/labels`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `LP-Labels-${receiptNumber.replace(/\//g, '-')}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  },

  // Inventory inbound wizard — receive stock with no matching PO.
  createUnmatched: async (data: UnmatchedReceiptCreate): Promise<GoodsReceipt> => {
    const response = await axiosInstance.post(`${BASE_URL}/receipts/unmatched`, data);
    return response.data.data;
  },

  // Reconcile an unmatched receipt to a real PO.
  linkToPurchaseOrder: async (
    receiptId: string,
    purchaseOrderId: string,
  ): Promise<GoodsReceipt> => {
    const response = await axiosInstance.post(`${BASE_URL}/receipts/${receiptId}/link`, {
      purchase_order_id: purchaseOrderId,
    });
    return response.data.data;
  },
};

export interface UnmatchedReceiptItem {
  product_id: string;
  variant_id?: string | null;
  quantity_received: number;
  unit_cost?: number;
  lot_id?: string | null;
  lot_code?: string | null;
  lot_expiry_date?: string | null;
  location_id?: string | null;
  packaging_id?: string | null;
  quantity_in_packaging?: number | null;
  description?: string | null;
  notes?: string | null;
}

export interface UnmatchedReceiptCreate {
  warehouse_id: string;
  vendor_id: string;
  company_id: string;
  reason_code: UnmatchedReceiptReasonCode;
  notes?: string | null;
  received_date?: string | null;
  items: UnmatchedReceiptItem[];
}

// ─── License Plate API (T6) ────────────────────────────────────

export const licensePlateApi = {
  list: async (params?: {
    purchase_order_id?: string;
    goods_receipt_id?: string;
    status?: LicensePlateStatus;
    lp_number?: string;
  }): Promise<LicensePlate[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/license-plates`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<LicensePlate> => {
    const response = await axiosInstance.get(`${BASE_URL}/license-plates/${id}`);
    return response.data.data;
  },

  create: async (data: LicensePlateCreate): Promise<LicensePlate> => {
    const response = await axiosInstance.post(`${BASE_URL}/license-plates`, data);
    return response.data.data;
  },

  bulkCreate: async (items: LicensePlateCreate[]): Promise<LicensePlate[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/license-plates/bulk`, items);
    return response.data.data;
  },

  update: async (id: string, data: Partial<LicensePlateCreate>): Promise<LicensePlate> => {
    const response = await axiosInstance.patch(`${BASE_URL}/license-plates/${id}`, data);
    return response.data.data;
  },

  verify: async (
    id: string,
    status: LicensePlateStatus = 'verified',
    notes?: string,
  ): Promise<LicensePlate> => {
    const response = await axiosInstance.post(`${BASE_URL}/license-plates/${id}/verify`, null, {
      params: { status, notes },
    });
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/license-plates/${id}`);
  },

  downloadLabel: async (id: string, lpNumber: string): Promise<void> => {
    const response = await axiosInstance.get(`${BASE_URL}/license-plates/${id}/label`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `LP-${lpNumber.replace(/\//g, '-')}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  },
};

// ─── Three-way match API (T6) ──────────────────────────────────

export const threeWayMatchApi = {
  forPo: async (poId: string): Promise<ThreeWayMatchReport> => {
    const response = await axiosInstance.get(`${BASE_URL}/orders/${poId}/three-way-match`);
    return response.data.data;
  },
};

// ─── AP bill from goods receipt (P2P-1) ───────────────────────
// Builds the vendor bill from a confirmed receipt. The bill is an accounting
// entity, reached here via the accounting REST endpoint (the sanctioned
// cross-module path) rather than a TS import — keeping the frontend module
// boundary intact. Returns the new draft bill id for navigation.
export const apBillFromReceiptApi = {
  create: async (goodsReceiptId: string): Promise<{ id: string }> => {
    const response = await axiosInstance.post('/api/v1/accounting/bills/from-receipt', {
      goods_receipt_id: goodsReceiptId,
    });
    return response.data.data;
  },
};

// ─── Dashboard API ─────────────────────────────────────────────

export const dashboardApi = {
  // On the shared KPI substrate: the response body IS the model — no {data: ...}
  // envelope to unwrap (docs/platform/dashboard-kpi-endpoints.md §1).
  get: async (): Promise<ProcurementDashboard> => {
    const response = await axiosInstance.get(`${BASE_URL}/dashboard/stats`);
    return response.data;
  },
};

// ─── Settings API ──────────────────────────────────────────────

export const settingsApi = {
  getTenantSettings: async (): Promise<ProcurementTenantSettings> => {
    const response = await axiosInstance.get(`${BASE_URL}/settings/tenant`);
    // Settings routes return the settings object flat (not the module's
    // {data: ...} envelope) — matches the inventory/support settings convention.
    return response.data;
  },

  updateTenantSettings: async (data: Partial<ProcurementTenantSettings>): Promise<ProcurementTenantSettings> => {
    const response = await axiosInstance.put(`${BASE_URL}/settings/tenant`, data);
    return response.data;
  },
};

// ─── Landed Cost API (T20C) ────────────────────────────────────

export const landedCostApi = {
  forOrder: async (orderId: string): Promise<LandedCostRollup> => {
    const response = await axiosInstance.get(`${BASE_URL}/orders/${orderId}/landed-cost`);
    return response.data.data;
  },

  forReceipt: async (receiptId: string): Promise<LandedCostRollup> => {
    const response = await axiosInstance.get(`${BASE_URL}/receipts/${receiptId}/landed-cost`);
    return response.data.data;
  },

  applyForOrder: async (orderId: string): Promise<LandedCostApplyResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/orders/${orderId}/landed-cost/apply`);
    return response.data.data;
  },

  applyForReceipt: async (receiptId: string): Promise<LandedCostApplyResult> => {
    const response = await axiosInstance.post(`${BASE_URL}/receipts/${receiptId}/landed-cost/apply`);
    return response.data.data;
  },
};

// ─── Supplier Claims API (T20C) ────────────────────────────────

export const supplierClaimApi = {
  // Server-paged (Shape A): one page of rows plus the size of the whole filtered
  // set, so the pager can never be driven by the row count.
  list: async (params?: ListSupplierClaimsParams): Promise<ProcurementPage<SupplierClaim>> => {
    const response = await axiosInstance.get(`${BASE_URL}/claims`, { params });
    return { items: response.data.data, total: response.data.pagination.total };
  },

  get: async (id: string): Promise<SupplierClaim> => {
    const response = await axiosInstance.get(`${BASE_URL}/claims/${id}`);
    return response.data.data;
  },

  create: async (data: SupplierClaimCreate): Promise<SupplierClaim> => {
    const response = await axiosInstance.post(`${BASE_URL}/claims`, data);
    return response.data.data;
  },

  update: async (id: string, data: SupplierClaimUpdate): Promise<SupplierClaim> => {
    const response = await axiosInstance.patch(`${BASE_URL}/claims/${id}`, data);
    return response.data.data;
  },

  transition: async (id: string, data: SupplierClaimTransition): Promise<SupplierClaim> => {
    const response = await axiosInstance.post(`${BASE_URL}/claims/${id}/transition`, data);
    return response.data.data;
  },

  deduct: async (id: string): Promise<SupplierClaim> => {
    const response = await axiosInstance.post(`${BASE_URL}/claims/${id}/deduct`);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/claims/${id}`);
  },
};

// ─── Vendor Returns API (RTV, SEC-2) ───────────────────────────

export const vendorReturnApi = {
  // Server-paged (Shape A). `indexes: null` so a multi-value `status` serializes
  // as the repeatable `?status=a&status=b` FastAPI reads, not `status[]=a`.
  list: async (params?: ListVendorReturnsParams): Promise<ProcurementPage<VendorReturn>> => {
    const response = await axiosInstance.get(`${BASE_URL}/vendor-returns/`, {
      params,
      paramsSerializer: { indexes: null },
    });
    return { items: response.data.items ?? [], total: response.data.total ?? 0 };
  },

  get: async (id: string): Promise<VendorReturn> => {
    const response = await axiosInstance.get(`${BASE_URL}/vendor-returns/${id}`);
    return response.data.data;
  },

  create: async (data: VendorReturnCreate): Promise<VendorReturn> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendor-returns/`, data);
    return response.data.data;
  },

  update: async (id: string, data: VendorReturnUpdate): Promise<VendorReturn> => {
    const response = await axiosInstance.patch(`${BASE_URL}/vendor-returns/${id}`, data);
    return response.data.data;
  },

  remove: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/vendor-returns/${id}`);
  },

  authorise: async (id: string): Promise<VendorReturn> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendor-returns/${id}/authorise`);
    return response.data.data;
  },

  ship: async (id: string): Promise<VendorReturn> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendor-returns/${id}/ship`);
    return response.data.data;
  },

  close: async (id: string): Promise<VendorReturn> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendor-returns/${id}/close`);
    return response.data.data;
  },

  cancel: async (id: string): Promise<VendorReturn> => {
    const response = await axiosInstance.post(`${BASE_URL}/vendor-returns/${id}/cancel`);
    return response.data.data;
  },

  createVendorCredit: async (id: string): Promise<CreateVendorCreditResult> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/vendor-returns/${id}/create-vendor-credit`,
    );
    return { vendorReturn: response.data.data, bill: response.data.bill };
  },
};

// ─── Receipt Reason Codes API ──────────────────────────────────

export const receiptReasonCodeApi = {
  list: async (kind?: 'reject' | 'damage'): Promise<ReceiptReasonCode[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/receipt-reason-codes`, {
      params: kind ? { kind } : undefined,
    });
    return response.data.data;
  },
};

// ─── Goods Receipt Attachments (the receiving slip) ─────────────

/**
 * Evidence files on a goods receipt. Deliberately NOT the generic
 * `/api/v1/files` surface: the slip carries a typed `attachment_kind`, the whole
 * photo set has to land in one transaction (the sibling ingestion path took a
 * file per request and orphaned all but the last), and the response tells the
 * caller whether it may write at all.
 */
export const goodsReceiptAttachmentApi = {
  list: async (receiptId: string): Promise<GoodsReceiptAttachmentListResponse> => {
    const response = await axiosInstance.get(`${BASE_URL}/receipts/${receiptId}/attachments`);
    return response.data;
  },

  upload: async (
    receiptId: string,
    files: File[],
    attachmentKind: GoodsReceiptAttachmentKind = 'receiving_slip',
  ): Promise<GoodsReceiptAttachmentListResponse> => {
    const form = new FormData();
    // One request for the whole set, so a slip photographed in three pages is
    // either wholly attached or wholly not.
    files.forEach((file) => form.append('files', file));
    form.append('attachment_kind', attachmentKind);
    const response = await axiosInstance.post(
      `${BASE_URL}/receipts/${receiptId}/attachments`,
      form,
    );
    return response.data;
  },

  remove: async (receiptId: string, fileId: string): Promise<GoodsReceiptAttachmentListResponse> => {
    const response = await axiosInstance.delete(
      `${BASE_URL}/receipts/${receiptId}/attachments/${fileId}`,
    );
    return response.data;
  },
};
